import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, ArrowRight, Briefcase, CheckCircle2, Clock3, Plane, Shield, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { PageHeader, Input, Labeled } from "./Rentals";
import { DatePicker, TimePicker, combineDateTime } from "../lib/date-pickers";
import { BookingSuccessDialog } from "../lib/booking-success";
import { PhoneField, PreferredContactMethodField } from "../components/PhoneField";
import { buildPhoneFieldValue, getPhoneFieldError, touchPhoneField } from "../lib/phone";
import { formatISK } from "../lib/currency";

const SERVICES = [
  {
    value: "airport_transfer",
    label: "Airport Transfer",
    subtitle: "KEF ↔ Reykjavík",
    icon: Plane,
    blurb: "Fixed-price airport runs with instant totals and direct booking.",
  },
  {
    value: "private_driver",
    label: "Private Driver",
    subtitle: "Hourly chauffeur",
    icon: Briefcase,
    blurb: "Direct hourly pricing, minimum 3 hours, ideal for meetings and flexible itineraries.",
  },
  {
    value: "day_tour",
    label: "Private Day Tours",
    subtitle: "8h / 10h transport-led",
    icon: Sparkles,
    blurb: "Transport-style private touring with direct fixed pricing.",
  },
];

function buildQuotePayload(serviceKind, form) {
  return {
    service_kind: serviceKind,
    start_at: combineDateTime(form.date, form.time).toISOString(),
    transfer_direction: serviceKind === "airport_transfer" ? form.transfer_direction : null,
    requested_hours: serviceKind === "private_driver" ? Number(form.requested_hours) || null : null,
    day_tour_hours: serviceKind === "day_tour" ? Number(form.day_tour_hours) || null : null,
    pickup_at_kef: serviceKind === "airport_transfer" ? form.transfer_direction === "arrival" : !!form.pickup_at_kef,
    extra_stop_count: Number(form.extra_stop_count) || 0,
  };
}

function bookingEndAt(serviceKind, start, form) {
  if (serviceKind === "private_driver") {
    return new Date(start.getTime() + (Number(form.requested_hours) || 3) * 60 * 60 * 1000);
  }
  if (serviceKind === "day_tour") {
    return new Date(start.getTime() + (Number(form.day_tour_hours) || 8) * 60 * 60 * 1000);
  }
  return new Date(start.getTime() + 2 * 60 * 60 * 1000);
}

function tierDisplay(quote) {
  if (!quote) return "DIRECT pricing";
  return `${String(quote.pricing_tier || "direct").toUpperCase()} pricing`;
}

function quoteHighlights(quote) {
  if (!quote) return [];
  const items = [quote.service_label];
  if (quote.night_transfer) items.push("Late-night start");
  if (quote.same_day) items.push("Same-day request");
  if (quote.pickup_at_kef) items.push("KEF pickup");
  if (quote.extra_stop_count) items.push(`${quote.extra_stop_count} extra ${quote.extra_stop_count === 1 ? "stop" : "stops"}`);
  return items;
}

export default function Transfers() {
  const today = new Date();
  const [serviceKind, setServiceKind] = useState("airport_transfer");
  const [form, setForm] = useState({
    date: today,
    time: "10:00",
    transfer_direction: "arrival",
    requested_hours: 3,
    day_tour_hours: 8,
    pickup_at_kef: false,
    extra_stop_count: 0,
    passengers: 2,
    customer_name: "",
    email: "",
    phone: buildPhoneFieldValue(),
    preferred_contact_method: null,
    pickup_custom_address: "",
    dropoff_custom_address: "",
    notes: "",
  });
  const [quote, setQuote] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [smartOptions, setSmartOptions] = useState(null);
  const [cityCenterExactRequired, setCityCenterExactRequired] = useState(true);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null);
  const pricingHighlights = useMemo(() => quoteHighlights(quote), [quote]);

  useEffect(() => {
    api.get("/transfer-locations").then(({ data }) => {
      const city = (data || []).find((loc) => loc.code === "reykjavik-city-center");
      if (city) setCityCenterExactRequired(!!city.requires_exact_address);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (serviceKind === "private_driver") {
      setForm((prev) => ({ ...prev, transfer_direction: "arrival" }));
    }
  }, [serviceKind]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.post("/transport/pricing-quote", buildQuotePayload(serviceKind, form));
        setQuote(data);
      } catch (err) {
        setQuote(null);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [serviceKind, form]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      try {
        const start = combineDateTime(form.date, form.time);
        const end = bookingEndAt(serviceKind, start, form);
        const { data } = await api.post("/availability/check", {
          product_type: "transport",
          requested_start: start.toISOString(),
          requested_end: serviceKind === "airport_transfer" ? null : end.toISOString(),
          service_kind: serviceKind,
          requested_hours: serviceKind === "private_driver" ? Number(form.requested_hours) || null : null,
          day_tour_hours: serviceKind === "day_tour" ? Number(form.day_tour_hours) || null : null,
          passengers: Number(form.passengers) || 1,
          slot_interval_minutes: serviceKind === "airport_transfer" ? 15 : 30,
        });
        setAvailability(data);
        if (!data.available && Array.isArray(data.next_available_options) && data.next_available_options.length > 0) {
          const recommended = data.next_available_options[0];
          setSmartOptions({
            message: data.message,
            options: data.next_available_options,
            recommendedStart: recommended.start,
          });
          const currentIso = start.toISOString();
          if (recommended.start !== currentIso) {
            applyAvailableTime(recommended.start, recommended.display_time);
          }
        } else if (!data.available) {
          setSmartOptions({ message: data.message, options: [], recommendedStart: null });
        }
      } catch {
        setAvailability(null);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [serviceKind, form]);

  const headline = useMemo(() => SERVICES.find((service) => service.value === serviceKind), [serviceKind]);
  const smartAvailability = useMemo(() => {
    if (!smartOptions) return availability;
    const currentStart = combineDateTime(form.date, form.time)?.toISOString();
    const matchesSuggested = smartOptions.options?.some((option) => option.start === currentStart);
    if (!matchesSuggested) return availability;
    return {
      available: false,
      message: "Sorry, that pickup time is not available. We selected the next available pickup time for you.",
      next_available_options: smartOptions.options,
      next_available_start: smartOptions.recommendedStart,
      next_available_display: smartOptions.options?.[0]?.display_time,
    };
  }, [availability, smartOptions, form.date, form.time]);

  const submit = async (e) => {
    e.preventDefault();
    if (smartAvailability && !smartAvailability.available) {
      toast.error(smartAvailability.message || "Please select an available pickup time.");
      return;
    }
    const phoneError = getPhoneFieldError(form.phone);
    if (phoneError) {
      setForm({ ...form, phone: touchPhoneField(form.phone) });
      toast.error(phoneError);
      return;
    }
    if (serviceKind === "private_driver" && Number(form.requested_hours) < 3) {
      toast.error("Private Driver requires a minimum of 3 hours");
      return;
    }
    const nextErrors = {};
    if (serviceKind === "airport_transfer" && cityCenterExactRequired) {
      if (form.transfer_direction === "arrival" && !String(form.dropoff_custom_address || "").trim()) {
        nextErrors.dropoff_custom_address = "Drop-off address / hotel is required.";
      }
      if (form.transfer_direction === "departure" && !String(form.pickup_custom_address || "").trim()) {
        nextErrors.pickup_custom_address = "Pickup address / hotel is required.";
      }
      if (Object.keys(nextErrors).length > 0) {
        setFieldErrors(nextErrors);
        toast.error(Object.values(nextErrors)[0]);
        return;
      }
    }
    setFieldErrors({});
    setSubmitting(true);
    try {
      const start = combineDateTime(form.date, form.time);
      const end = bookingEndAt(serviceKind, start, form);
      const { data } = await api.post("/bookings", {
        service_type: "transport",
        transport_service_kind: serviceKind,
        transfer_direction: serviceKind === "airport_transfer" ? form.transfer_direction : null,
        pickup_custom_address: form.pickup_custom_address || null,
        dropoff_custom_address: form.dropoff_custom_address || null,
        requested_hours: serviceKind === "private_driver" ? Number(form.requested_hours) || null : null,
        day_tour_hours: serviceKind === "day_tour" ? Number(form.day_tour_hours) || null : null,
        pickup_at_kef: serviceKind === "airport_transfer" ? form.transfer_direction === "arrival" : !!form.pickup_at_kef,
        extra_stop_count: Number(form.extra_stop_count) || 0,
        start_at: start.toISOString(),
        end_at: end.toISOString(),
        customer_name: form.customer_name,
        email: form.email,
        phone: form.phone.e164 || null,
        phone_country: form.phone.country || null,
        preferred_contact_method: form.preferred_contact_method || null,
        passengers: Number(form.passengers) || 1,
        notes: form.notes || null,
      });
      if (data.invoice_mode === "online_card" && data.status === "pending_payment" && data.pricing_mode === "fixed_price") {
        const checkout = await api.post("/payments/checkout", {
          booking_id: data.id,
          origin_url: window.location.origin,
        });
        sessionStorage.setItem("icefleet_pay_booking", data.id);
        window.location.href = checkout.data.url;
        return;
      }
      toast.success("Transport request submitted — fixed price saved.");
      setCreated(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const applyAvailableTime = (start, displayTime) => {
    if (!start) return;
    const d = new Date(start);
    const time = displayTime || `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
    setForm((prev) => ({ ...prev, date: d, time }));
  };

  return (
    <div>
      <PageHeader
        eyebrow="Transport services"
        title="Airport transfers, private drivers, and fixed-price day tours."
        subtitle="Direct fixed pricing for airport transfers, private drivers, and day tours."
      />

      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-10 space-y-6">
        <div className="grid gap-4 lg:grid-cols-3" data-testid="transport-service-switcher">
          {SERVICES.map((service) => {
            const Icon = service.icon;
            const active = serviceKind === service.value;
            return (
              <button
                key={service.value}
                type="button"
                onClick={() => setServiceKind(service.value)}
                data-testid={`transport-service-${service.value}`}
                className={`rounded-2xl border p-5 text-left transition-all ${
                  active ? "border-slate-900 bg-slate-900 text-white shadow-lg" : "border-slate-200 bg-white text-slate-900 hover:border-slate-300"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className={`text-xs font-bold uppercase tracking-[0.2em] ${active ? "text-sky-200" : "text-sky-700"}`}>{service.subtitle}</p>
                    <h3 className="mt-2 font-heading text-xl font-semibold">{service.label}</h3>
                  </div>
                  <Icon size={18} className={active ? "text-white" : "text-sky-700"} />
                </div>
                <p className={`mt-3 text-sm ${active ? "text-slate-200" : "text-slate-600"}`}>{service.blurb}</p>
              </button>
            );
          })}
        </div>

        <div className="grid gap-8 lg:grid-cols-[1.25fr_0.75fr]">
          <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 space-y-6" data-testid="transport-pricing-form">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-sky-700">{headline?.subtitle}</p>
                <h2 className="mt-2 font-heading text-3xl font-semibold tracking-tight">{headline?.label}</h2>
                <p className="mt-2 text-sm text-slate-600">{headline?.blurb}</p>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Labeled label="Date">
                <DatePicker value={form.date} onChange={(date) => setForm({ ...form, date })} min={today} data-testid="transport-date" />
              </Labeled>
              <Labeled label="Start time">
                <TimePicker value={form.time} onChange={(time) => setForm({ ...form, time })} data-testid="transport-time" step={serviceKind === "airport_transfer" ? 15 : 30} />
              </Labeled>
              {serviceKind === "airport_transfer" ? (
                <Labeled label="Direction">
                  <select data-testid="transport-direction" value={form.transfer_direction} onChange={(e) => setForm({ ...form, transfer_direction: e.target.value })} className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-slate-900">
                    <option value="arrival">KEF → Reykjavík (Hotel / Address required)</option>
                    <option value="departure">Reykjavík (Hotel / Address required) → KEF</option>
                  </select>
                </Labeled>
              ) : serviceKind === "private_driver" ? (
                <Labeled label="Hours">
                  <select data-testid="transport-hours" value={form.requested_hours} onChange={(e) => setForm({ ...form, requested_hours: e.target.value })} className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-slate-900">
                    {[3, 4, 5, 6, 8, 10].map((hours) => <option key={hours} value={hours}>{hours} hours</option>)}
                  </select>
                </Labeled>
              ) : (
                <Labeled label="Day tour length">
                  <select data-testid="transport-day-tour-hours" value={form.day_tour_hours} onChange={(e) => setForm({ ...form, day_tour_hours: e.target.value })} className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-slate-900">
                    <option value="8">8 hours</option>
                    <option value="10">10 hours</option>
                  </select>
                </Labeled>
              )}
              <Labeled label="Passengers">
                <Input data-testid="transport-passengers" type="number" min="1" max="8" value={form.passengers} onChange={(e) => setForm({ ...form, passengers: e.target.value })} />
              </Labeled>
              {(serviceKind === "private_driver" || serviceKind === "day_tour") && (
                <label className="flex items-center gap-2 text-sm text-slate-700" data-testid="transport-kef-pickup-row">
                  <input type="checkbox" checked={!!form.pickup_at_kef} onChange={(e) => setForm({ ...form, pickup_at_kef: e.target.checked })} data-testid="transport-kef-pickup" className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900" />
                  Pickup starts at KEF (+3.000 kr)
                </label>
              )}
              <Labeled label="Extra stops">
                <select data-testid="transport-extra-stops" value={form.extra_stop_count} onChange={(e) => setForm({ ...form, extra_stop_count: e.target.value })} className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-slate-900">
                  {[0, 1, 2, 3].map((count) => <option key={count} value={count}>{count} {count === 1 ? "stop" : "stops"}</option>)}
                </select>
              </Labeled>
            </div>

            {serviceKind === "airport_transfer" && cityCenterExactRequired && form.transfer_direction === "arrival" && (
              <Labeled label="Drop-off address / hotel">
                <>
                  <Input data-testid="transport-dropoff-address" required value={form.dropoff_custom_address} onChange={(e) => setForm({ ...form, dropoff_custom_address: e.target.value })} />
                  <p className="mt-1 text-xs text-slate-500" data-testid="transport-dropoff-address-helper">Enter hotel name or exact street address</p>
                  {fieldErrors.dropoff_custom_address ? <p className="mt-1 text-xs font-medium text-red-600" data-testid="transport-dropoff-address-error">{fieldErrors.dropoff_custom_address}</p> : null}
                </>
              </Labeled>
            )}
            {serviceKind === "airport_transfer" && cityCenterExactRequired && form.transfer_direction === "departure" && (
              <Labeled label="Pickup address / hotel">
                <>
                  <Input data-testid="transport-pickup-address" required value={form.pickup_custom_address} onChange={(e) => setForm({ ...form, pickup_custom_address: e.target.value })} />
                  <p className="mt-1 text-xs text-slate-500" data-testid="transport-pickup-address-helper">Enter hotel name or exact street address</p>
                  {fieldErrors.pickup_custom_address ? <p className="mt-1 text-xs font-medium text-red-600" data-testid="transport-pickup-address-error">{fieldErrors.pickup_custom_address}</p> : null}
                </>
              </Labeled>
            )}

            <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-5" data-testid="transport-price-card">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-sky-700">{tierDisplay(quote)}</p>
                  <p className="mt-2 font-heading text-4xl font-semibold tracking-tight text-slate-900" data-testid="transport-total-price">
                    {quote ? formatISK(quote.fixed_price_isk) : "Calculating…"}
                  </p>
                  <p className="mt-3 text-sm font-semibold text-slate-900" data-testid="transport-pay-now-copy">Secure your booking now</p>
                  {pricingHighlights.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2" data-testid="transport-quote-highlights">
                      {pricingHighlights.map((item) => (
                        <span key={item} className="inline-flex items-center rounded-full bg-sky-100 px-3 py-1 text-xs font-semibold text-sky-900 ring-1 ring-inset ring-sky-200">
                          {item}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="mt-3 flex flex-wrap gap-2">
                    {(quote?.badges || ["Fixed price", "Private service", "VAT included"]).map((badge) => (
                      <span key={badge} className="inline-flex items-center rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-700 ring-1 ring-inset ring-slate-200">
                        {badge}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="rounded-full bg-white p-3 text-sky-700 shadow-sm">
                  <Shield size={18} />
                </div>
              </div>

              {quote?.breakdown?.length ? (
                <div className="mt-5 space-y-2 text-sm text-slate-600" data-testid="transport-breakdown-list">
                  {quote.breakdown.map((item) => (
                    <div key={item.label} className="flex items-center justify-between gap-3 border-b border-slate-200/70 pb-2 last:border-b-0 last:pb-0">
                      <span>{item.label}</span>
                      <span className="font-semibold text-slate-900 tabular-nums">{formatISK(item.amount_isk)}</span>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Labeled label="Full name">
                <Input data-testid="transport-name" required value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} />
              </Labeled>
              <Labeled label="Email">
                <Input data-testid="transport-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </Labeled>
              <Labeled label="Phone / WhatsApp">
                <PhoneField value={form.phone} onChange={(phone) => setForm({ ...form, phone })} countryTestId="transport-phone-country" numberTestId="transport-phone" errorTestId="transport-phone-error" />
              </Labeled>
              <Labeled label="Preferred contact method">
                <PreferredContactMethodField value={form.preferred_contact_method} onChange={(preferred_contact_method) => setForm({ ...form, preferred_contact_method })} testid="transport-contact-method" />
              </Labeled>
            </div>

            <Labeled label="Notes (optional)">
              <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="transport-notes" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-900" placeholder="Flight number, stop details, route notes…" />
            </Labeled>

            {smartAvailability && !smartAvailability.available && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="transport-availability-warning">
                <p className="flex items-start gap-2 font-medium"><AlertTriangle size={16} className="mt-0.5" /> {smartAvailability.message}</p>
                {Array.isArray(smartAvailability.next_available_options) && smartAvailability.next_available_options.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2" data-testid="transport-next-available-options">
                    {smartAvailability.next_available_options.map((option, index) => (
                      <button key={option.start} type="button" onClick={() => applyAvailableTime(option.start, option.display_time)} data-testid={`transport-use-next-${option.display_time}`} className={`w-full sm:w-auto rounded-md border px-3 py-2 text-xs font-semibold transition-colors ${index === 0 ? "border-slate-900 bg-slate-900 text-white" : "border-amber-700 text-amber-900 hover:bg-amber-100"}`}>
                        {index === 0 ? `Recommended · ${option.display_time}` : option.display_time}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <button type="submit" disabled={submitting || !quote || (smartAvailability && !smartAvailability.available)} data-testid="transport-submit" className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60">
              {submitting ? "Redirecting to payment…" : "Pay & confirm booking"} <ArrowRight size={14} />
            </button>
          </form>

          <aside className="space-y-5">
            <div className="rounded-2xl border border-slate-200 bg-white p-6">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-sky-700">Pricing logic</p>
              <ul className="mt-4 space-y-3 text-sm text-slate-600">
                <li className="flex items-start gap-2"><CheckCircle2 size={16} className="mt-0.5 text-emerald-600" /> Direct pricing is the default on the public frontend.</li>
                <li className="flex items-start gap-2"><CheckCircle2 size={16} className="mt-0.5 text-emerald-600" /> Same-day requests auto-add 15% to the subtotal.</li>
                <li className="flex items-start gap-2"><CheckCircle2 size={16} className="mt-0.5 text-emerald-600" /> Late-night starts (22:00–06:00) auto-add 5.000 kr across all transport services.</li>
              </ul>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-900 p-6 text-white">
              <div className="flex items-center gap-2 text-sky-300"><Clock3 size={16} /><span className="text-xs font-bold uppercase tracking-[0.2em]">Ready for payment</span></div>
              <p className="mt-3 text-sm text-slate-300">
                All transport totals are calculated and stored in kr so the booking is ready for Rapyd checkout immediately after submission.
              </p>
            </div>
          </aside>
        </div>
      </section>

      {created && <BookingSuccessDialog booking={created} onClose={() => setCreated(null)} />}
    </div>
  );
}